import React, { useEffect, useState } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import { useIsAdmin } from 'hooks/useIsAdmin'
import { LOCAL, DEVELOP } from 'constants/env'
import UsersTable from './components/UsersTable'
import NicheStats from './components/NicheStats'
import ApiQuotaCards from './components/ApiQuotaCards'

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

export default function AdminDashboard(): JSX.Element {
  const { isAdmin, loading: adminLoading } = useIsAdmin()
  const [users, setUsers] = useState<any[]>([])
  const [nicheStats, setNicheStats] = useState<any>(null)
  const [apiQuota, setApiQuota] = useState<any>(null)
  const [recentErrors, setRecentErrors] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!isAdmin || adminLoading) return

    const loadData = async (): Promise<void> => {
      try {
        const fetchJson = async (url: string): Promise<any> => {
          const res = await fetch(url)
          const data = await res.json()
          if (!res.ok) console.error(`${url} failed:`, data)
          return data
        }
        const [usersData, nicheData, quotaData] = await Promise.all([
          fetchJson(`${config.API_URL}/api/admin/users`),
          fetchJson(`${config.API_URL}/api/admin/niche-stats`),
          fetchJson(`${config.API_URL}/api/admin/api-quota`),
        ])
        if (usersData.success) {
          setUsers(usersData.users)
        }
        if (nicheData.success) setNicheStats(nicheData)
        if (quotaData.success) {
          setApiQuota(quotaData.quota)
          setRecentErrors(quotaData.recentErrors ?? [])
        }
      } catch (err) {
        console.error('Failed to load admin data:', err)
      } finally {
        setLoading(false)
      }
    }
    void loadData()
  }, [isAdmin, adminLoading])

  if (adminLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '100px 0' }}>
        <CircularProgress sx={{ color: '#a855f7' }} />
      </Box>
    )
  }

  if (!isAdmin) {
    return (
      <Box sx={{ padding: '100px 0', textAlign: 'center' }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '18px' }}>
          Not authorized
        </Typography>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '100px 0' }}>
        <CircularProgress sx={{ color: '#a855f7' }} />
      </Box>
    )
  }

  return (
    <Box sx={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      <Typography
        sx={{
          fontSize: '28px',
          fontWeight: 700,
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '32px',
        }}
      >
        Admin Dashboard
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        <ApiQuotaCards quota={apiQuota} recentErrors={recentErrors} />
        <NicheStats stats={nicheStats} />
        <UsersTable users={users} />
      </Box>
    </Box>
  )
}
